import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { FadeUp } from "../components/Motion";
import { toast } from "sonner";
import { Loader2, Sparkles, Upload, Camera, ArrowRight, ArrowDown, Square, LayoutGrid, Columns4, Trees, Image as ImageIcon } from "lucide-react";
import LimitReachedPanel from "../components/LimitReachedPanel";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import PageMeta from "../components/PageMeta";
import { trackLead } from "../lib/fbpixel";
import { trackGaLead } from "../lib/ga";
import { trackFormStart } from "../lib/formTracking";

const BUDGETS = [
  "Under $10,000",
  "$10,000 - $25,000",
  "$25,000 - $50,000",
  "$50,000 - $100,000",
  "$100,000+",
];

const FEATURES = [
  "Patio", "Retaining Wall", "Fire Pit", "Pergola", "Deck", "BBQ Area",
  "Outdoor Kitchen", "Water Feature", "Pond", "Pool", "Trees", "Shrubs",
  "Flowers", "Lighting", "Artificial Turf", "Privacy Screens", "Irrigation",
];

const HERO_BEFORE = "https://static.prod-images.emergentagent.com/jobs/1f829ff4-86fc-4a70-85e2-bf7576b5a535/images/0b2fbf6ba05e82848ffd188af633502eae3ee9dc78d931ec9798f73c374d2db7.png";
const HERO_AFTER = "https://static.prod-images.emergentagent.com/jobs/1f829ff4-86fc-4a70-85e2-bf7576b5a535/images/32f6119be643cb94304b9a8494c3625658bae83795e8fed147257ebd817b8daa.png";

const STYLES = ["Modern", "Luxury", "Contemporary", "Natural", "Family Friendly", "Low Maintenance"];

export default function YardDesigner() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    width_ft: 40, depth_ft: 30, notes: "",
    budget: BUDGETS[2], features: ["Patio", "Fire Pit", "Lighting"], style: "Modern",
    photo_base64: null,
  });
  const [lead, setLead] = useState({ name: "", email: "", phone: "", address: "", marketing_consent: false });
  const [concepts, setConcepts] = useState(null);
  const [cached, setCached] = useState(false);
  const [capped, setCapped] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, label: "" });
  const [emailStatus, setEmailStatus] = useState(null); // {used, limit, remaining}
  const [confirmAck, setConfirmAck] = useState(false);
  const [generationId, setGenerationId] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [selectingLabel, setSelectingLabel] = useState(null);

  // Check remaining generations whenever a valid email is entered on step 4
  useEffect(() => {
    const e = (lead.email || "").trim();
    if (step !== 4 || !e.includes("@") || e.length < 5) {
      setEmailStatus(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/yard-designer/status?email=${encodeURIComponent(e)}`);
        setEmailStatus(r.data);
      } catch {
        setEmailStatus(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [lead.email, step]);

  const toggle = (f) => setForm((p) => ({
    ...p, features: p.features.includes(f) ? p.features.filter(x => x !== f) : [...p.features, f],
  }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress / resize on the client so iPhone photos (5–10 MB) don't break the upload
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        setForm((p) => ({ ...p, photo_base64: dataUrl.split(",")[1] }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    setLoading(true);
    setProgress({ pct: 5, label: "Initializing AI designer…" });

    // simulated progress while server works (real progress would need SSE)
    const stages = [
      { at: 800,  pct: 18, label: "Composing concept directions…" },
      { at: 4000, pct: 35, label: "Drafting concept A…" },
      { at: 18000, pct: 52, label: "Rendering concept A image…" },
      { at: 32000, pct: 68, label: "Rendering concept B image…" },
      { at: 50000, pct: 82, label: "Rendering concept C image…" },
      { at: 75000, pct: 92, label: "Finalizing renders (large download)…" },
    ];
    const timers = stages.map((s) => setTimeout(() => setProgress({ pct: s.pct, label: s.label }), s.at));

    try {
      const payload = { ...form, ...lead };
      const r = await api.post("/yard-designer/generate", payload, { timeout: 300000 });
      // Lead is captured server-side regardless of outcome — fire Pixel Lead event.
      trackLead({
        content_category: "yard_designer",
        source_page: "yard-designer",
        budget: form.budget,
        style: form.style,
      });
      trackGaLead({ lead_type: "yard_designer", source_page: "yard-designer" });
      setProgress({ pct: 100, label: "Done." });
      if (r.data.status === "capped") {
        setCapped(r.data.message);
        setLimitReached(false);
        setStep(5);
      } else if (r.data.status === "limit_reached") {
        setCapped(r.data.message);
        setLimitReached(true);
        setConcepts(r.data.concepts || []);
        setGenerationId(r.data.generation_id || null);
        setSelectedLabel(r.data.selected_concept_label || null);
        setCached(true);
        setStep(5);
      } else {
        setConcepts(r.data.concepts);
        setGenerationId(r.data.generation_id || null);
        setSelectedLabel(null);
        setCached(!!r.data.cached);
        setLimitReached(false);
        setStep(5);
        if (r.data.cached) {
          toast.info("Welcome back — showing your previously generated concepts.", { duration: 6000 });
        }
      }
      setTimeout(() => {
        document.querySelector("[data-testid='designer-results']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 402 || (detail && detail.toLowerCase().includes("budget"))) {
        msg = "AI budget exhausted. Please top up your Emergent Universal Key (Profile → Universal Key → Add Balance) and try again.";
      } else if (status === 429) {
        msg = detail || "You've reached today's daily limit. Please try again tomorrow.";
      } else if (e?.code === "ECONNABORTED") {
        msg = "Generation took longer than expected. Please try again — the AI is sometimes slower under load.";
      } else if (detail) {
        msg = detail;
      } else {
        msg = "Generation failed. Please try again.";
      }
      toast.error(msg, { duration: 10000 });
    } finally {
      timers.forEach(clearTimeout);
      setLoading(false);
    }
  };

  const chooseConcept = async (label) => {
    if (!generationId) {
      toast.error("Missing generation reference — please refresh and try again.");
      return;
    }
    setSelectingLabel(label);
    try {
      await api.post("/yard-designer/select", {
        email: lead.email,
        generation_id: generationId,
        concept_label: label,
      });
      setSelectedLabel(label);
      toast.success(`Concept ${label} selected. Taking you to your account…`, { duration: 4000 });
      setTimeout(() => {
        navigate(`/account?email=${encodeURIComponent(lead.email)}`);
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your selection. Please try again.");
    } finally {
      setSelectingLabel(null);
    }
  };

  const input = "w-full bg-[#151515] border border-[#D8D6D0]/20 px-4 py-3 text-sm text-[#F5F3EF] placeholder:text-[#D8D6D0]/40 focus:outline-none focus:border-[#F5F3EF]/70";

  return (
    <div data-testid="yard-designer-page" className="pt-28 md:pt-32 pb-32 px-4 md:px-12 lg:px-16">
      <PageMeta
        title="AI Yard Designer — Upload a Photo, Get 3 Concepts in 90 Seconds"
        description="Upload a backyard photo and our AI generates three luxury landscape concepts with renders, budget ranges, and timelines. Reviewed by a senior NorthCrest designer."
        path="/yard-designer"
        image="https://static.prod-images.emergentagent.com/jobs/1f829ff4-86fc-4a70-85e2-bf7576b5a535/images/c4af177f33aee6c44095ba236753cd625709b6759f91efdc7f4a17903dc09c5c.png"
      />
      {/* HERO — compact, with live Before/After example */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Yard Designer</div>
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Turn Your Yard Photo Into <span className="italic text-[#D8D6D0]">3 Landscape Design Ideas</span>
          </h1>
          <p className="text-[#D8D6D0]/65 mt-6 max-w-xl text-lg">
            Upload a photo, choose what you want to add, and see new possibilities for your yard.
          </p>
          <button
            data-testid="designer-hero-cta"
            onClick={() => document.getElementById("designer-start")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase transition-colors"
          >
            Start My Yard Design <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-4 text-xs uppercase tracking-widest text-[#D8D6D0]/50">
            Free to try &nbsp;•&nbsp; No commitment &nbsp;•&nbsp; About 90 seconds
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="aspect-[4/3] relative border border-[#D8D6D0]/15" data-testid="hero-before-after">
            <BeforeAfterSlider
              beforeImage={HERO_BEFORE}
              afterImage={HERO_AFTER}
              beforeLabel="Before"
              afterLabel="AI Design"
              alt="AI yard design example"
            />
          </div>
          <div className="mt-3 text-center text-xs text-[#D8D6D0]/45 uppercase tracking-widest">Drag the slider — a real AI transformation</div>
        </FadeUp>
      </section>

      {/* HOW IT WORKS — 3-step infographic */}
      <section className="mt-20 md:mt-24" data-testid="how-it-works">
        <FadeUp>
          <h2 className="font-serif-display text-3xl md:text-4xl text-center">
            Your New Yard in <span className="italic text-[#D8D6D0]">3 Simple Steps</span>
          </h2>
        </FadeUp>
        <div className="mt-10 flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-0">
          {[
            {
              n: "01",
              title: "Upload Your Yard",
              body: "Upload a clear photo of your outdoor space.",
              icons: [Camera],
            },
            {
              n: "02",
              title: "Choose Your Ideas",
              body: "Select the features and style you like.",
              icons: [Square, LayoutGrid, Columns4, Trees],
            },
            {
              n: "03",
              title: "See Your Design",
              body: "Receive personalized concepts for your yard.",
              icons: [ImageIcon],
            },
          ].map((s, i) => (
            <div key={s.n} className="flex flex-col md:flex-row items-center md:flex-1">
              <FadeUp delay={i * 0.08} className="w-full h-full">
                <div data-testid={`how-step-${s.n}`} className="nc-card h-full p-8 text-center flex flex-col items-center">
                  <div className="flex items-center gap-3 text-[#8C1D30]">
                    {s.icons.map((Ic) => <Ic key={Ic.displayName || Ic.name} className="w-7 h-7" strokeWidth={1.5} />)}
                  </div>
                  <div className="overline text-[#D8D6D0]/50 mt-5">{s.n}</div>
                  <div className="font-serif-display text-2xl mt-2">{s.title}</div>
                  <p className="text-sm text-[#D8D6D0]/60 mt-3 max-w-[240px]">{s.body}</p>
                </div>
              </FadeUp>
              {i < 2 && (
                <>
                  <ArrowRight className="hidden md:block w-6 h-6 mx-3 text-[#8C1D30] flex-shrink-0" />
                  <ArrowDown className="md:hidden w-6 h-6 my-1 text-[#8C1D30]" />
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <div id="designer-start" className="mt-16 scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <FadeUp className="lg:col-span-8">
          <div className="nc-card p-5 md:p-12">
            {/* Stepper */}
            <div className="flex gap-2 mb-10">
              {[1,2,3,4].map(n => (
                <div key={n} className={`flex-1 h-px ${step >= n ? "bg-[#8C1D30]" : "bg-[#D8D6D0]/20"}`} />
              ))}
            </div>

            {step === 1 && (
              <div data-testid="designer-step-1">
                <div className="overline text-[#D8D6D0]/50 mb-3">Step 01 — Property</div>
                <h2 className="font-serif-display text-3xl mb-8">Your space.</h2>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="overline text-[#D8D6D0]/60 block mb-2">Width (ft)</label>
                    <input data-testid="designer-width" type="number" className={input} value={form.width_ft} onChange={(e) => setForm({ ...form, width_ft: +e.target.value })} />
                  </div>
                  <div>
                    <label className="overline text-[#D8D6D0]/60 block mb-2">Depth (ft)</label>
                    <input data-testid="designer-depth" type="number" className={input} value={form.depth_ft} onChange={(e) => setForm({ ...form, depth_ft: +e.target.value })} />
                  </div>
                </div>
                <label className="overline text-[#D8D6D0]/60 block mb-2">Notes</label>
                <textarea data-testid="designer-notes" rows={3} className={input} placeholder="Sun exposure, slope, existing features…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <label className="overline text-[#D8D6D0]/60 block mb-2 mt-6">
                  Backyard Photo <span className="text-[#8C1D30]">— strongly recommended</span>
                </label>
                <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-[#D8D6D0]/30 cursor-pointer hover:border-[#F5F3EF]/60 transition-colors">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm text-[#D8D6D0]/70">{form.photo_base64 ? "Photo attached — AI will transform this exact yard" : "Upload backyard photo (AI edits your real space)"}</span>
                  <input data-testid="designer-photo" type="file" accept="image/*" onChange={onFile} className="hidden" />
                </label>
                {form.photo_base64 && (
                  <div className="mt-3">
                    <img src={`data:image/jpeg;base64,${form.photo_base64}`} alt="preview" className="max-h-40 border border-[#D8D6D0]/15" />
                  </div>
                )}
                <p className="text-xs text-[#D8D6D0]/50 mt-3 leading-relaxed">
                  With a photo, our AI keeps your existing house, fence, and perspective — and transforms only the yard. Without a photo, the AI generates concepts from scratch.
                </p>
                <div className="mt-10 flex justify-end">
                  <button data-testid="designer-next-1" onClick={() => setStep(2)} className="px-8 py-4 bg-[#F5F3EF] text-[#151515] text-sm tracking-widest uppercase hover:bg-[#D8D6D0] transition-colors">Continue</button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="designer-step-2">
                <div className="overline text-[#D8D6D0]/50 mb-3">Step 02 — Budget & Features</div>
                <h2 className="font-serif-display text-3xl mb-8">Investment & elements.</h2>
                <label className="overline text-[#D8D6D0]/60 block mb-3">Budget</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-8">
                  {BUDGETS.map((b) => (
                    <button key={b} data-testid={`budget-${b}`} onClick={() => setForm({ ...form, budget: b })} className={`px-4 py-3 text-sm text-left transition-colors border ${form.budget === b ? "bg-[#0B2B1F] border-[#F5F3EF]/60 text-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"}`}>{b}</button>
                  ))}
                </div>
                <label className="overline text-[#D8D6D0]/60 block mb-3">Features</label>
                <div className="flex flex-wrap gap-2">
                  {FEATURES.map((f) => (
                    <button key={f} data-testid={`feature-${f}`} onClick={() => toggle(f)} className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors border ${form.features.includes(f) ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"}`}>{f}</button>
                  ))}
                </div>
                <div className="mt-10 flex justify-between">
                  <button onClick={() => setStep(1)} className="px-6 py-4 text-sm uppercase tracking-widest text-[#D8D6D0]/70 hover:text-[#F5F3EF]">← Back</button>
                  <button data-testid="designer-next-2" onClick={() => setStep(3)} className="px-8 py-4 bg-[#F5F3EF] text-[#151515] text-sm tracking-widest uppercase hover:bg-[#D8D6D0]">Continue</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="designer-step-3">
                <div className="overline text-[#D8D6D0]/50 mb-3">Step 03 — Style</div>
                <h2 className="font-serif-display text-3xl mb-8">Aesthetic direction.</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {STYLES.map((s) => (
                    <button key={s} data-testid={`style-${s}`} onClick={() => setForm({ ...form, style: s })} className={`px-4 py-6 text-sm transition-colors border ${form.style === s ? "bg-[#0B2B1F] border-[#F5F3EF]/60" : "border-[#D8D6D0]/20 hover:border-[#D8D6D0]/50"}`}>{s}</button>
                  ))}
                </div>
                <div className="mt-10 flex justify-between">
                  <button onClick={() => setStep(2)} className="px-6 py-4 text-sm uppercase tracking-widest text-[#D8D6D0]/70 hover:text-[#F5F3EF]">← Back</button>
                  <button data-testid="designer-next-3" onClick={() => setStep(4)} className="px-8 py-4 bg-[#F5F3EF] text-[#151515] text-sm tracking-widest uppercase hover:bg-[#D8D6D0]">Continue</button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="designer-step-4">
                <div className="overline text-[#D8D6D0]/50 mb-3">Step 04 — Contact</div>
                <h2 className="font-serif-display text-3xl mb-3">Where shall we send your concepts?</h2>

                {/* Lifetime limit warning — always visible, asks for confirmation */}
                <div className="mt-2 mb-8 border-l-2 border-[#8C1D30] bg-[#8C1D30]/10 p-5">
                  <div className="overline text-[#8C1D30] mb-2">Please read carefully</div>
                  <p className="text-sm text-[#F5F3EF] leading-relaxed">
                    Each email is allowed <span className="font-semibold">3 AI generations total — for life</span>. Each generation produces 3 concept variations.
                  </p>
                  <p className="text-sm text-[#D8D6D0]/80 leading-relaxed mt-2">
                    Please choose your features and style thoughtfully before generating. After your 3 generations, a senior NorthCrest designer will craft any further variations personally.
                  </p>
                  {emailStatus && (
                    <div className="mt-4 pt-4 border-t border-[#8C1D30]/30 flex items-center justify-between" data-testid="email-status">
                      <div className="text-xs text-[#D8D6D0]/80">
                        For <span className="text-[#F5F3EF]">{emailStatus.email}</span>: <span className="text-[#F5F3EF]">{emailStatus.used}</span> used / <span className="text-[#F5F3EF]">{emailStatus.limit}</span> total
                      </div>
                      <div className={`overline ${emailStatus.remaining === 0 ? "text-[#8C1D30]" : "text-[#F5F3EF]"}`}>
                        {emailStatus.remaining} {emailStatus.remaining === 1 ? "left" : "remaining"}
                      </div>
                    </div>
                  )}
                  <label className="flex items-start gap-3 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="designer-acknowledge"
                      checked={confirmAck}
                      onChange={(e) => setConfirmAck(e.target.checked)}
                      className="mt-1 accent-[#8C1D30]"
                    />
                    <span className="text-xs text-[#D8D6D0]/80 leading-relaxed">
                      I understand each email is limited to 3 lifetime AI generations and I've chosen my features carefully.
                    </span>
                  </label>
                </div>

                <form
                  data-testid="designer-lead-form"
                  onSubmit={(e) => { e.preventDefault(); generate(); }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input required placeholder="Full name" className={input} value={lead.name} onFocus={() => trackFormStart("yard-designer")} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="designer-lead-name" />
                    <input required type="email" placeholder="Email" className={input} value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="designer-lead-email" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input required minLength={7} pattern="[0-9\s\(\)\+\.\-]{7,}" title="Please enter a valid phone number" placeholder="Phone" className={input} value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="designer-lead-phone" />
                    <input placeholder="Project address (optional)" className={input} value={lead.address} onChange={(e) => setLead({ ...lead, address: e.target.value })} data-testid="designer-lead-address" />
                  </div>
                  <label className="flex items-start gap-3 text-xs text-[#D8D6D0]/60 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="designer-consent-marketing"
                      checked={lead.marketing_consent}
                      onChange={(e) => setLead({ ...lead, marketing_consent: e.target.checked })}
                      className="mt-0.5 w-4 h-4 accent-[#8C1D30] flex-shrink-0"
                    />
                    <span>I'd like to receive occasional landscaping tips and offers from NorthCrest Landscaping. You can unsubscribe anytime.</span>
                  </label>
                  <p className="text-xs text-[#D8D6D0]/40 leading-relaxed">
                    By generating concepts you consent to NorthCrest Landscaping collecting this information to respond to your project request — see our <a href="/privacy-policy" data-testid="designer-consent-marketing-privacy-link" className="underline underline-offset-2 hover:text-[#8C1D30]">Privacy Policy</a>. We never share your information.
                  </p>
                  <div className="flex justify-between items-center pt-2">
                    <button type="button" onClick={() => setStep(3)} className="px-6 py-4 text-sm uppercase tracking-widest text-[#D8D6D0]/70 hover:text-[#F5F3EF]">← Back</button>
                    <button
                      type="submit"
                      data-testid="designer-generate"
                      disabled={loading || !confirmAck || (emailStatus && emailStatus.remaining === 0)}
                      className="px-10 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={!confirmAck ? "Please acknowledge the 3-generation limit first" : ""}
                    >
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate My Concepts</>}
                    </button>
                  </div>
                </form>
                {emailStatus && emailStatus.remaining === 0 && (
                  <div className="mt-6">
                    <LimitReachedPanel
                      variant="limit_reached"
                      message={`You've already used all ${emailStatus.limit} AI generations for ${emailStatus.email}. Our designers will continue working with you personally — no AI needed.`}
                      lead={{ ...lead, email: emailStatus.email }}
                      form={form}
                    />
                  </div>
                )}
                {loading && (
                  <div data-testid="designer-progress" className="mt-8 nc-card p-6">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-sm text-[#F5F3EF] flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#8C1D30]" />
                        {progress.label || "Working…"}
                      </div>
                      <div className="text-xs text-[#D8D6D0]/60 tabular-nums">{progress.pct}%</div>
                    </div>
                    <div className="h-1 bg-[#D8D6D0]/10 overflow-hidden">
                      <div className="h-full bg-[#8C1D30] transition-all duration-700 ease-out" style={{ width: `${progress.pct}%` }} />
                    </div>
                    <div className="mt-4 text-xs text-[#D8D6D0]/50 leading-relaxed">
                      AI is composing your three concepts and rendering bespoke photography. Typically <span className="text-[#F5F3EF]">60–120 seconds</span>. Please keep this tab open.
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 5 && capped && !concepts && (
              <LimitReachedPanel
                variant="capped"
                message={capped}
                lead={lead}
                form={form}
              />
            )}

            {step === 5 && concepts && (
              <div data-testid="designer-results">
                {limitReached && (
                  <LimitReachedPanel
                    variant="limit_reached"
                    message={capped}
                    lead={lead}
                    form={form}
                  />
                )}
                <div className="overline text-[#D8D6D0]/50 mb-3">{cached ? "Your Saved Concepts" : "Your Concepts"}</div>
                <h2 className="font-serif-display text-3xl mb-2">Three directions for your space.</h2>
                {cached && !limitReached && (
                  <p className="text-xs text-[#D8D6D0]/50 mb-8">These were generated previously for your email. To create new concepts, please contact our team.</p>
                )}
                <div className="space-y-10 mt-6">
                  {concepts.map((c) => (
                    <div key={c.label} data-testid={`concept-${c.label}`} className={`border overflow-hidden ${selectedLabel === c.label ? "border-[#8C1D30]" : "border-[#D8D6D0]/10"}`}>
                      <div className="relative aspect-[16/9] overflow-hidden bg-[#0a0a0a]">
                        {c.image_base64 ? (
                          <img src={`data:image/png;base64,${c.image_base64}`} alt={c.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-[#D8D6D0]/50 text-sm gap-2 p-6 text-center">
                            <span>Render unavailable for this concept.</span>
                          </div>
                        )}
                        {selectedLabel === c.label && (
                          <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-[#8C1D30] text-[#F5F3EF] text-[10px] uppercase tracking-widest">
                            ✓ Selected
                          </div>
                        )}
                      </div>
                      <div className="p-8">
                        <div className="overline text-[#8C1D30] mb-2">Concept {c.label}</div>
                        <div className="font-serif-display text-3xl mb-4">{c.title}</div>
                        <p className="text-[#D8D6D0]/70 mb-6">{c.description}</p>
                        <div className="flex flex-wrap gap-2 mb-6">
                          {(c.features_summary || []).map((f) => (
                            <span key={f} className="px-3 py-1 text-xs uppercase tracking-wider border border-[#D8D6D0]/20 text-[#D8D6D0]/80">{f}</span>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#D8D6D0]/10">
                          <div>
                            <div className="overline text-[#D8D6D0]/50">Budget</div>
                            <div className="font-serif-display text-xl mt-1">{c.budget_estimate}</div>
                          </div>
                          <div>
                            <div className="overline text-[#D8D6D0]/50">Timeline</div>
                            <div className="font-serif-display text-xl mt-1">{c.timeline_estimate}</div>
                          </div>
                        </div>
                        {c.image_base64 && (
                          <button
                            onClick={() => chooseConcept(c.label)}
                            disabled={selectingLabel !== null || selectedLabel === c.label}
                            data-testid={`choose-concept-${c.label}`}
                            className={`mt-6 w-full px-6 py-4 text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                              selectedLabel === c.label
                                ? "bg-[#0B2B1F] border border-[#8C1D30] text-[#F5F3EF]"
                                : "bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF]"
                            }`}
                          >
                            {selectingLabel === c.label
                              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                              : selectedLabel === c.label
                              ? <>✓ Selected</>
                              : <>Choose & Continue →</>}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 nc-card p-8 text-center">
                  <div className="font-serif-display text-2xl">{selectedLabel ? "Your selection is saved." : "Your concepts are saved."}</div>
                  <p className="text-[#D8D6D0]/60 mt-2">A senior designer at NorthCrest will reach out within 24 hours to develop your concept further.</p>
                  <a
                    href={lead.email ? `/account?email=${encodeURIComponent(lead.email)}` : "/account"}
                    className="inline-block mt-6 px-8 py-4 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors"
                    data-testid="goto-account"
                  >
                    View My Account →
                  </a>
                </div>
              </div>
            )}
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-4">
          <div className="nc-card p-8 sticky top-28">
            <div className="overline text-[#D8D6D0]/50 mb-4">Summary</div>
            <div className="space-y-4 text-sm">
              <div><span className="text-[#D8D6D0]/50">Size:</span> {form.width_ft} × {form.depth_ft} ft</div>
              <div><span className="text-[#D8D6D0]/50">Budget:</span> {form.budget}</div>
              <div><span className="text-[#D8D6D0]/50">Style:</span> {form.style}</div>
              <div><span className="text-[#D8D6D0]/50">Features:</span> {form.features.join(", ") || "—"}</div>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
